// ===== 십성(十星) + 지장간(地藏干) 계산 엔진 =====

import { HEAVENLY_STEMS, HEAVENLY_STEMS_KOR, STEM_ELEMENTS, BRANCH_ELEMENTS, type Element, type SajuResult } from './sajuCalculations';

// ===== 오행 인덱스 (목=0 화=1 토=2 금=3 수=4) =====
const ELEMENT_IDX: Record<Element, number> = { 목: 0, 화: 1, 토: 2, 금: 3, 수: 4 };
const ELEMENTS_ARR: Element[] = ['목', '화', '토', '금', '수'];

// 내가 생하는 오행 (목→화→토→금→수→목)
const GENERATES: number[] = [1, 2, 3, 4, 0];
// 내가 극하는 오행 (목극토, 화극금, 토극수, 금극목, 수극화)
const CONTROLS: number[] = [2, 3, 4, 0, 1];
// 나를 생하는 오행 (수생목, 목생화, 화생토, 토생금, 금생수)
const GENERATED_BY: number[] = [4, 0, 1, 2, 3];
// 나를 극하는 오행 (금극목, 수극화, 목극토, 화극금, 토극수)
const CONTROLLED_BY: number[] = [3, 4, 0, 1, 2];

// ===== 십성 정의 =====
export interface Sipseong {
  name: string;        // 십성명 (한자)
  kor: string;         // 한글명
  short: string;       // 약칭
  enName: string;      // English name
  enKeyword: string;   // English keyword
  category: 'bijep' | 'siksang' | 'jaeseong' | 'gwanseong' | 'inseong';
  description: string;
  keyword: string;
  color: string;
  bg: string;
}

export const SIPSEONG_MAP: Record<string, Sipseong> = {
  비견: { name: '비견', kor: '비견(比肩)', short: '비견', enName: 'Companion', enKeyword: 'Independence · Self', category: 'bijep', description: '나와 같은 기운. 독립적이고 주체적인 성향을 나타내요.', keyword: '독립·자존심', color: '#15803D', bg: '#DCFCE7' },
  겁재: { name: '겁재', kor: '겁재(劫財)', short: '겁재', enName: 'Rob Wealth', enKeyword: 'Competition · Drive', category: 'bijep', description: '강한 경쟁심과 추진력. 때로는 충동적으로 행동할 수 있어요.', keyword: '경쟁·추진력', color: '#166534', bg: '#BBF7D0' },
  식신: { name: '식신', kor: '식신(食神)', short: '식신', enName: 'Eating God', enKeyword: 'Expression · Ease', category: 'siksang', description: '먹고 즐기는 기운. 여유롭고 표현력이 풍부해요.', keyword: '표현·여유', color: '#B45309', bg: '#FEF3C7' },
  상관: { name: '상관', kor: '상관(傷官)', short: '상관', enName: 'Hurt Officer', enKeyword: 'Creativity · Arts', category: 'siksang', description: '창의적이고 예술적 재능. 틀에 얽매이지 않아요.', keyword: '창의·예술', color: '#92400E', bg: '#FDE68A' },
  편재: { name: '편재', kor: '편재(偏財)', short: '편재', enName: 'Indirect Wealth', enKeyword: 'Ventures · Invest', category: 'jaeseong', description: '사업적 재능과 돈을 다루는 능력. 기회를 잘 잡아요.', keyword: '사업·투자', color: '#DC2626', bg: '#FEE2E2' },
  정재: { name: '정재', kor: '정재(正財)', short: '정재', enName: 'Direct Wealth', enKeyword: 'Diligence · Stability', category: 'jaeseong', description: '성실한 노력으로 모은 재물. 꾸준하고 안정적이에요.', keyword: '성실·안정', color: '#991B1B', bg: '#FECACA' },
  편관: { name: '편관', kor: '편관(偏官)', short: '편관', enName: '7 Killings', enKeyword: 'Power · Leadership', category: 'gwanseong', description: '강한 추진력과 권력 지향. 리더십이 강해요.', keyword: '권력·리더십', color: '#1E3A5F', bg: '#DBEAFE' },
  정관: { name: '정관', kor: '정관(正官)', short: '정관', enName: 'Direct Officer', enKeyword: 'Principle · Trust', category: 'gwanseong', description: '법과 원칙을 중시하는 공정한 기운. 신뢰받아요.', keyword: '원칙·신뢰', color: '#1E40AF', bg: '#BFDBFE' },
  편인: { name: '편인', kor: '편인(偏印)', short: '편인', enName: 'Indirect Resource', enKeyword: 'Intuition · Insight', category: 'inseong', description: '직관과 영감. 독특한 재능과 학문적 기질이 있어요.', keyword: '직관·영감', color: '#6B7280', bg: '#F3F4F6' },
  정인: { name: '정인', kor: '정인(正印)', short: '정인', enName: 'Direct Resource', enKeyword: 'Wisdom · Learning', category: 'inseong', description: '학문적 능력과 어머니의 사랑 같은 기운. 지혜로워요.', keyword: '학문·지혜', color: '#374151', bg: '#E5E7EB' },
};

// ===== 십성별 스토리 해석 =====
export interface SipseongStory {
  career: { headline: string; headlineEn: string; detail: string; detailEn: string; jobs: string[]; jobsEn: string[] };
  love: { headline: string; headlineEn: string; detail: string; detailEn: string; style: string; styleEn: string };
  wealth: { headline: string; headlineEn: string; detail: string; detailEn: string; tip: string; tipEn: string };
  advice: string;
  adviceEn: string;
}

export const SIPSEONG_STORY: Record<string, SipseongStory> = {
  비견: {
    career: {
      headline: '독립적인 자영업자 or 전문가',
      headlineEn: 'Independent freelancer or specialist',
      detail: '남의 지시보다 내 방식대로 일할 때 빛나요. 직장에서도 자기 영역을 확실히 구축하는 스타일이에요.',
      detailEn: 'You shine when working your own way. Even inside an organization, you carve out a territory distinctly your own.',
      jobs: ['프리랜서', '자영업', '전문직', '운동선수', '1인 크리에이터'],
      jobsEn: ['Freelancer', 'Self-employed', 'Professional', 'Athlete', 'Solo Creator'],
    },
    love: {
      headline: '대등한 파트너십을 원해요',
      headlineEn: 'Seeking an equal partnership',
      detail: '나를 존중해주는 파트너를 찾아요. 지나치게 의존하거나 간섭하는 관계는 금방 답답해져요.',
      detailEn: "You seek a partner who respects your independence. Overly clingy or intrusive relationships feel suffocating fast.",
      style: '독립적·대등한 관계 선호',
      styleEn: 'Independent · Equal relationship preferred',
    },
    wealth: {
      headline: '내 힘으로 번 돈을 소중히 해요',
      headlineEn: 'Money earned with your own hands',
      detail: '투자보다는 직접 노동과 실력으로 버는 것이 맞아요. 경쟁에서 이기면 수입이 늘어나요.',
      detailEn: 'Direct work and skill-building suit you more than passive investment. Income grows when you win the competition.',
      tip: '경쟁이 있는 분야에서 실력을 쌓으면 돈이 따라와요.',
      tipEn: 'Build your skills in a competitive field — the money follows naturally.',
    },
    advice: '자존감은 강점이에요. 하지만 팀워크도 때론 필요해요. 혼자 다 짊어지려 하지 마세요.',
    adviceEn: "Self-confidence is your strength. But teamwork is sometimes essential. Don't try to carry everything alone.",
  },
  겁재: {
    career: {
      headline: '승부사 기질의 사업가',
      headlineEn: "A gambler's soul — born for business",
      detail: '경쟁을 즐기고 위험을 감수하는 배짱이 있어요. 조직보다 사업이나 영업 쪽이 잘 맞아요.',
      detailEn: 'You love competition and have the guts to take risks. Business and sales suit you better than corporate structures.',
      jobs: ['영업직', '스타트업 창업', '투자·트레이더', '스포츠', '중개업'],
      jobsEn: ['Sales', 'Startup founder', 'Investor / Trader', 'Sports', 'Broker'],
    },
    love: {
      headline: '강렬하고 열정적인 연애',
      headlineEn: 'Intense and passionate relationships',
      detail: '한번 마음이 가면 올인하는 타입이에요. 질투심도 강한 편이라 상대가 힘들 수 있어요.',
      detailEn: 'Once you fall for someone, you go all in. Your jealousy can run high, which may overwhelm your partner.',
      style: '열정적·독점욕이 강한 스타일',
      styleEn: 'Passionate · Possessive style',
    },
    wealth: {
      headline: '크게 벌고 크게 쓰는 타입',
      headlineEn: 'Big earner, big spender',
      detail: '한탕 심리가 있어서 투기성 투자에 끌릴 수 있어요. 안정보다 리스크를 선택하는 경향이 있어요.',
      detailEn: "A get-rich-quick mindset may pull you toward speculation. You tend to choose risk over stability.",
      tip: '투기보다 사업 수완으로 돈을 버는 게 장기적으로 유리해요.',
      tipEn: 'Long-term, business acumen beats speculation every time.',
    },
    advice: '에너지가 넘치는 건 장점이에요. 다만 충동적 결정은 한 번 더 생각하고 실행하세요.',
    adviceEn: 'Your energy is a gift. But impulsive decisions deserve one extra beat of thought — that pause makes all the difference.',
  },
  식신: {
    career: {
      headline: '표현하고 먹여주는 직업이 찰떡',
      headlineEn: 'Jobs where you express and nurture',
      detail: '재능을 즐겁게 발휘하는 환경에서 최고의 성과를 내요. 억압적인 환경은 맞지 않아요.',
      detailEn: 'You perform best where you can freely exercise your talents. Suppressive, rigid environments really do not suit you.',
      jobs: ['요리사·식품업', '강사·교육', '예술·디자인', '작가', '방송·유튜버'],
      jobsEn: ['Chef / Food industry', 'Instructor / Education', 'Art & Design', 'Writer', 'Broadcasting / Creator'],
    },
    love: {
      headline: '편안하고 따뜻한 연애를 원해요',
      headlineEn: 'Warm and cozy relationships',
      detail: '함께 있으면 배부르고 따뜻한 느낌의 관계를 좋아해요. 상대를 잘 챙기고 표현이 자연스러워요.',
      detailEn: 'You love relationships that feel full and warm. You naturally care for your partner and express affection freely.',
      style: '여유롭고 배려심 깊은 관계 선호',
      styleEn: 'Relaxed and deeply caring style',
    },
    wealth: {
      headline: '즐기면서 버는 재주가 있어요',
      headlineEn: 'Getting paid for what you enjoy',
      detail: '좋아하는 일을 하면 돈이 따라오는 타입이에요. 억지로 하는 일은 수입도 지지부진해요.',
      detailEn: 'Money tends to follow when you do what you love. Forced work leads to stagnant income.',
      tip: '취미와 직업의 경계를 허물면 자연스럽게 부가 따라와요.',
      tipEn: 'Blur the line between your hobby and career — wealth follows naturally.',
    },
    advice: '여유로운 삶이 가장 잘 맞아요. 너무 많은 걸 욕심내기보다 즐겁게 꾸준히 하는 게 비결이에요.',
    adviceEn: 'An easy-going life suits you best. Rather than wanting everything at once, joyful consistency is your secret weapon.',
  },
  상관: {
    career: {
      headline: '규칙을 깨는 창의적 아웃사이더',
      headlineEn: 'A creative outsider who breaks the rules',
      detail: '기존 방식에 의문을 품고 새로운 것을 만들어내요. 보수적인 조직보다 자유로운 환경이 맞아요.',
      detailEn: 'You question the existing way and create something new. Free environments suit you far better than conservative orgs.',
      jobs: ['예술가·작가', '개발자', '디자이너', '방송인', '혁신가·기획자'],
      jobsEn: ['Artist / Writer', 'Developer', 'Designer', 'Broadcaster', 'Innovator / Planner'],
    },
    love: {
      headline: '이상형이 분명하고 잘 변해요',
      headlineEn: 'A clear but shifting ideal type',
      detail: '이상이 높아서 현실의 상대에게 실망하기 쉬워요. 자유로운 연애를 좋아하고 구속을 싫어해요.',
      detailEn: "High ideals mean you're easily disappointed by real partners. You love freedom in romance and resist being tied down.",
      style: '자유롭고 지적 교감을 중시하는 스타일',
      styleEn: 'Free-spirited, values intellectual connection',
    },
    wealth: {
      headline: '아이디어를 돈으로 바꾸는 능력',
      headlineEn: 'The ability to turn ideas into money',
      detail: '특허·창작물·콘텐츠 등 남들이 생각 못 한 방법으로 돈을 버는 재주가 있어요.',
      detailEn: 'You have a knack for earning through patents, creative works, or content in ways others never think of.',
      tip: '아이디어를 구체적 상품으로 만드는 실행력을 키우면 대박 가능해요.',
      tipEn: 'Pair your ideas with execution — turning them into real products opens the door to a big breakthrough.',
    },
    advice: '뛰어난 두뇌가 무기예요. 단, 말이 너무 직설적이면 관계가 틀어질 수 있어요. 한 박자 쉬고 말하세요.',
    adviceEn: 'Your sharp mind is your weapon. But overly blunt speech can damage relationships. Pause one beat before you speak.',
  },
  편재: {
    career: {
      headline: '사업가·투자가 기질이 다분해요',
      headlineEn: 'Born with the instincts of an entrepreneur',
      detail: '기회를 먼저 발견하고 움직이는 능력이 있어요. 고정 월급보다 성과급·수수료·사업 수익이 맞아요.',
      detailEn: 'You spot opportunities first and move fast. Variable income from commissions and business suits you over a fixed salary.',
      jobs: ['사업가', '부동산·주식 투자', '영업·세일즈', '무역·유통', '마케터'],
      jobsEn: ['Entrepreneur', 'Real estate / Stocks', 'Sales', 'Trade & Distribution', 'Marketer'],
    },
    love: {
      headline: '넓고 다양한 이성 관계',
      headlineEn: 'Wide and varied romantic connections',
      detail: '이성에게 인기가 많고 스킨십과 표현이 자연스러워요. 한 사람에게 집중하기 어려울 수 있어요.',
      detailEn: "You're popular with the opposite sex — affection and expression come naturally. Focusing on one person can be a challenge.",
      style: '활발하고 자유로운 연애 스타일',
      styleEn: 'Active and free-spirited romantic style',
    },
    wealth: {
      headline: '돈 냄새를 잘 맡는 타입',
      headlineEn: 'A natural nose for money',
      detail: '재물이 들어오는 흐름을 잘 읽어요. 하지만 들어오는 속도만큼 나가는 속도도 빨라요.',
      detailEn: 'You read financial flows well. But the speed money comes in tends to match the speed it goes out.',
      tip: '수익의 30%는 무조건 저축·재투자하는 원칙을 세우세요.',
      tipEn: 'Commit to saving and reinvesting 30% of all income — no exceptions, no shortcuts.',
    },
    advice: '타고난 사업 감각이 있어요. 하지만 도박성 투자는 금물! 정보와 분석을 기반으로 움직이세요.',
    adviceEn: 'Your innate business sense is a genuine gift. But avoid gambling-style investments — always move on solid data and analysis.',
  },
  정재: {
    career: {
      headline: '착실하게 성과 내는 전문가',
      headlineEn: 'A steady professional who delivers results',
      detail: '꾸준히 실력을 쌓아 안정적인 포지션을 만드는 게 최선이에요. 규칙적인 직장 생활에 잘 적응해요.',
      detailEn: 'Consistently building skills to secure a stable position is your best path. You adapt well to structured, regular work life.',
      jobs: ['회계사·세무사', '공무원·공기업', '금융·은행', '의사·약사', '관리직'],
      jobsEn: ['Accountant / Tax advisor', 'Civil servant', 'Finance / Banking', 'Doctor / Pharmacist', 'Management'],
    },
    love: {
      headline: '책임감 있는 진지한 연애',
      headlineEn: 'Serious and responsible relationships',
      detail: '연애도 진지하게 접근하고 결혼을 염두에 두는 편이에요. 상대를 꼼꼼히 살피고 신중하게 선택해요.',
      detailEn: 'You approach romance seriously with marriage in mind. You observe carefully and choose your partner with great thought.',
      style: '안정적·계획적 관계 지향',
      styleEn: 'Stable and planned relationship orientation',
    },
    wealth: {
      headline: '저축의 귀재, 꾸준한 자산 증식',
      headlineEn: 'Master of saving, steady asset growth',
      detail: '화려한 한방보다 착실한 저축과 안전한 투자로 자산을 불려가요. 장기적으로 부자가 되는 타입이에요.',
      detailEn: 'Rather than a windfall, you grow wealth through disciplined saving and safe investments. You are built for long-term prosperity.',
      tip: '복리의 마법을 믿고 장기 적금·인덱스 펀드를 꾸준히 유지하세요.',
      tipEn: 'Trust the magic of compound interest — stick to long-term savings and index funds without wavering.',
    },
    advice: '성실함은 최고의 무기예요. 다만 너무 보수적이면 기회를 놓칠 수 있어요. 가끔은 도전해봐요.',
    adviceEn: 'Diligence is your ultimate weapon. But being overly conservative risks missing opportunities. Challenge yourself sometimes.',
  },
  편관: {
    career: {
      headline: '권력과 영향력을 추구해요',
      headlineEn: 'Seeking power and influence',
      detail: '강한 추진력과 결단력으로 조직을 이끌어요. 도전적인 환경에서 두각을 나타내요.',
      detailEn: 'You lead organizations with strong drive and decisive action. You naturally stand out in challenging environments.',
      jobs: ['경찰·군인·검사', 'CEO·임원', '정치인', '스포츠 코치', '특수직'],
      jobsEn: ['Police / Military / Prosecutor', 'CEO / Executive', 'Politician', 'Sports coach', 'Special forces'],
    },
    love: {
      headline: '강렬하고 지배적인 관계',
      headlineEn: 'Intense and dominant relationships',
      detail: '주도권을 쥐려는 경향이 있어요. 강한 상대에게 매력을 느끼지만 충돌도 잦아요.',
      detailEn: 'You tend to want control. You are attracted to strong, assertive partners but clashes arise frequently.',
      style: '강렬하고 주도적인 연애 스타일',
      styleEn: 'Intense and dominant romantic style',
    },
    wealth: {
      headline: '큰 판을 벌이는 스타일',
      headlineEn: 'Playing for big stakes',
      detail: '소소한 이익보다 큰 프로젝트, 큰 계약을 선호해요. 위험을 감수하는 대신 큰 수익을 노려요.',
      detailEn: 'You prefer large projects and big contracts over small gains. You accept risk in exchange for big rewards.',
      tip: '법적·제도적 리스크를 항상 체크하는 습관이 재산을 지켜줘요.',
      tipEn: 'Always checking legal and regulatory risks is the habit that protects your wealth long-term.',
    },
    advice: '강한 에너지는 큰 성과를 만들어요. 하지만 주변 사람들과 협력하는 법을 배우면 더 멀리 갈 수 있어요.',
    adviceEn: 'Strong energy creates great results. Learning to collaborate with those around you will take you even further.',
  },
  정관: {
    career: {
      headline: '법과 원칙 안에서 빛나는 리더',
      headlineEn: 'A principled leader who shines within the rules',
      detail: '공정하고 체계적인 환경에서 최고의 성과를 내요. 조직의 신뢰를 받는 믿음직한 인재예요.',
      detailEn: 'You produce your best results in fair, systematic environments. You are the trustworthy talent every organization relies on.',
      jobs: ['공무원·판사', '대기업 관리직', '교수·연구원', '의사·법조인', '국회의원'],
      jobsEn: ['Civil servant / Judge', 'Corporate management', 'Professor / Researcher', 'Doctor / Lawyer', 'Legislator'],
    },
    love: {
      headline: '책임감 있는 이상적 파트너',
      headlineEn: 'A responsible and ideal partner',
      detail: '연애도 책임감 있게 임해요. 가정적이고 신뢰할 수 있는 파트너를 원하고, 자신도 그렇게 행동해요.',
      detailEn: 'You bring accountability to relationships. You want a family-oriented, trustworthy partner — and you aim to be exactly that yourself.',
      style: '안정적이고 원칙적인 관계 추구',
      styleEn: 'Stable and principled relationship pursuit',
    },
    wealth: {
      headline: '안정적이고 꾸준한 수입',
      headlineEn: 'Steady and consistent income',
      detail: '급여·수당·연금 등 고정 수입이 안정적으로 쌓여요. 횡재보다 꾸준한 성장이 체질에 맞아요.',
      detailEn: 'Fixed income from salary, allowances, and pensions accumulates steadily. Consistent growth suits your nature better than windfalls.',
      tip: '부동산이나 배당주 같은 장기 안정 자산이 잘 맞아요.',
      tipEn: 'Long-term stable assets like real estate or dividend stocks are a great fit for your energy.',
    },
    advice: '원칙을 지키는 것이 결국 가장 빠른 길이에요. 때로는 유연하게 상황을 읽는 지혜도 필요해요.',
    adviceEn: 'Sticking to your principles is ultimately the fastest path. Sometimes flexible wisdom in reading a situation is also needed.',
  },
  편인: {
    career: {
      headline: '독특한 분야의 전문가·연구자',
      headlineEn: 'An expert or researcher in unusual fields',
      detail: '남들이 보지 못하는 것을 보는 직관력이 있어요. 비주류나 특수 분야에서 두각을 나타내요.',
      detailEn: 'You see what others cannot. You naturally stand out in unconventional or highly specialized fields.',
      jobs: ['연구자·학자', '예술·음악', '점술·상담', '작가·철학자', '의학·심리학'],
      jobsEn: ['Researcher / Scholar', 'Art / Music', 'Divination / Counseling', 'Writer / Philosopher', 'Medicine / Psychology'],
    },
    love: {
      headline: '신비롭고 독특한 매력의 연애',
      headlineEn: 'Mysterious and uniquely magnetic in love',
      detail: '연애에서도 독특한 매력을 발산해요. 상대를 이해하는 깊은 공감 능력이 있지만 변덕스러울 수 있어요.',
      detailEn: 'You radiate a distinctive charm even in love. Deep empathy is your gift, but unpredictability can catch partners off guard.',
      style: '신비롭고 직관적인 연애 스타일',
      styleEn: 'Mysterious and intuitive romantic style',
    },
    wealth: {
      headline: '불규칙하지만 독특한 수입원',
      headlineEn: 'Irregular but uniquely creative income',
      detail: '남들이 생각 못 한 특이한 방식으로 돈을 버는 경우가 많아요. 수입이 들쭉날쭉할 수 있어요.',
      detailEn: 'You often earn in ways others never imagined. Income tends to swing between feast and famine.',
      tip: '불규칙 수입을 대비해 비상금 3~6개월치를 항상 유지하세요.',
      tipEn: 'Keep 3–6 months of emergency funds ready at all times to weather the income fluctuations.',
    },
    advice: '남다른 재능이 보물이에요. 단, 너무 몽상가적으로 살면 현실이 힘들어져요. 실행력을 키워보세요.',
    adviceEn: 'Your unique talent is a treasure. But living too much in your head makes real life hard. Build your execution muscle.',
  },
  정인: {
    career: {
      headline: '지식을 나누는 학문·교육 분야',
      headlineEn: 'Academia and education — sharing knowledge',
      detail: '배우고 가르치는 것이 천직이에요. 깊이 있는 지식과 안정적인 환경에서 빛나요.',
      detailEn: 'Learning and teaching is your true calling. You thrive with deep expertise in stable, nurturing environments.',
      jobs: ['교사·교수', '연구원', '의사·한의사', '작가·번역가', '공무원·사서'],
      jobsEn: ['Teacher / Professor', 'Researcher', 'Doctor / Oriental medicine', 'Writer / Translator', 'Civil servant / Librarian'],
    },
    love: {
      headline: '정서적 교감이 깊은 연애',
      headlineEn: 'Deeply emotionally connected romance',
      detail: '지적이고 배려심 있는 파트너에게 끌려요. 정신적인 연결을 매우 중요시해요.',
      detailEn: "You're drawn to intellectual and caring partners. Spiritual and emotional connection matters enormously to you.",
      style: '지적이고 안정적인 관계 지향',
      styleEn: 'Intellectual and stable relationship orientation',
    },
    wealth: {
      headline: '지식과 자격증이 곧 재산',
      headlineEn: 'Knowledge and credentials are your wealth',
      detail: '전문 자격증이나 학위가 장기적인 수입의 밑바탕이 돼요. 투자보다 자기계발에 쓰는 돈이 가장 큰 수익이에요.',
      detailEn: 'Professional credentials and degrees form the long-term foundation of your income. Self-investment beats financial investment.',
      tip: '전문성을 높이는 교육투자가 가장 ROI가 높아요.',
      tipEn: 'Education investment that builds genuine expertise delivers the highest return on investment.',
    },
    advice: '배움에 끝이 없는 사람이에요. 아는 것에서 그치지 말고 실제로 적용하고 나눠보세요.',
    adviceEn: "You are a lifelong learner. Don't stop at knowing — apply what you learn and generously share it with others.",
  },
};

// ===== 천간 인덱스로 십성 계산 =====
export function getSipseong(dayIdx: number, targetIdx: number): Sipseong {
  const dayEl = ELEMENT_IDX[STEM_ELEMENTS[dayIdx]];
  const targetEl = ELEMENT_IDX[STEM_ELEMENTS[targetIdx]];
  const dayYin = dayIdx % 2; // 0=양, 1=음
  const targetYin = targetIdx % 2;

  if (targetEl === dayEl) {
    return SIPSEONG_MAP[dayYin === targetYin ? '비견' : '겁재'];
  }
  if (targetEl === GENERATES[dayEl]) {
    return SIPSEONG_MAP[dayYin === targetYin ? '식신' : '상관'];
  }
  if (targetEl === CONTROLS[dayEl]) {
    return SIPSEONG_MAP[dayYin === targetYin ? '편재' : '정재'];
  }
  if (targetEl === CONTROLLED_BY[dayEl]) {
    return SIPSEONG_MAP[dayYin === targetYin ? '편관' : '정관'];
  }
  if (targetEl === GENERATED_BY[dayEl]) {
    return SIPSEONG_MAP[dayYin === targetYin ? '편인' : '정인'];
  }
  return SIPSEONG_MAP['비견'];
}

// ===== 지장간 데이터 =====
// 각 지지의 숨은 천간 [초기, 중기, 정기] (일수 기준, 정기가 마지막)
export interface JijangEntry {
  stemIdx: number; // 천간 인덱스
  days: number;
  role: '초기' | '중기' | '정기';
}

export const JIJANG: JijangEntry[][] = [
  // 子: 壬(10) 癸(20)
  [{ stemIdx: 8, days: 10, role: '초기' }, { stemIdx: 9, days: 20, role: '정기' }],
  // 丑: 癸(9) 辛(3) 己(18)
  [{ stemIdx: 9, days: 9, role: '초기' }, { stemIdx: 7, days: 3, role: '중기' }, { stemIdx: 5, days: 18, role: '정기' }],
  // 寅: 戊(7) 丙(7) 甲(16)
  [{ stemIdx: 4, days: 7, role: '초기' }, { stemIdx: 2, days: 7, role: '중기' }, { stemIdx: 0, days: 16, role: '정기' }],
  // 卯: 甲(10) 乙(20)
  [{ stemIdx: 0, days: 10, role: '초기' }, { stemIdx: 1, days: 20, role: '정기' }],
  // 辰: 乙(9) 癸(3) 戊(18)
  [{ stemIdx: 1, days: 9, role: '초기' }, { stemIdx: 9, days: 3, role: '중기' }, { stemIdx: 4, days: 18, role: '정기' }],
  // 巳: 戊(7) 庚(7) 丙(16)
  [{ stemIdx: 4, days: 7, role: '초기' }, { stemIdx: 6, days: 7, role: '중기' }, { stemIdx: 2, days: 16, role: '정기' }],
  // 午: 丙(10) 己(10) 丁(10)
  [{ stemIdx: 2, days: 10, role: '초기' }, { stemIdx: 5, days: 10, role: '중기' }, { stemIdx: 3, days: 10, role: '정기' }],
  // 未: 丁(9) 乙(3) 己(18)
  [{ stemIdx: 3, days: 9, role: '초기' }, { stemIdx: 1, days: 3, role: '중기' }, { stemIdx: 5, days: 18, role: '정기' }],
  // 申: 戊(7) 壬(7) 庚(16)
  [{ stemIdx: 4, days: 7, role: '초기' }, { stemIdx: 8, days: 7, role: '중기' }, { stemIdx: 6, days: 16, role: '정기' }],
  // 酉: 庚(10) 辛(20)
  [{ stemIdx: 6, days: 10, role: '초기' }, { stemIdx: 7, days: 20, role: '정기' }],
  // 戌: 辛(9) 丁(3) 戊(18)
  [{ stemIdx: 7, days: 9, role: '초기' }, { stemIdx: 3, days: 3, role: '중기' }, { stemIdx: 4, days: 18, role: '정기' }],
  // 亥: 戊(7) 甲(7) 壬(16)
  [{ stemIdx: 4, days: 7, role: '초기' }, { stemIdx: 0, days: 7, role: '중기' }, { stemIdx: 8, days: 16, role: '정기' }],
];

// ===== 십성 분석 결과 =====
export interface SipseongPillar {
  pillarLabel: string;
  stemSipseong: Sipseong | null; // null이면 일주 자신
  branchSipseong: Sipseong;
  jijang: Array<{ entry: JijangEntry; sipseong: Sipseong; stemName: string; stemKor: string }>;
}

export interface SipseongAnalysis {
  pillars: SipseongPillar[];
  dominant: Sipseong;        // 가장 많이 나오는 십성
  hidden: Sipseong;          // 지장간에서 가장 많이 숨은 십성
  allCounts: Record<string, number>;
  hiddenCounts: Record<string, number>;
}

export function analyzeSipseong(result: SajuResult): SipseongAnalysis {
  const dayIdx = result.dayPillar.stemIdx;
  const pillarsToAnalyze = [
    result.yearPillar,
    result.monthPillar,
    result.dayPillar,
    ...(result.hourPillar ? [result.hourPillar] : []),
  ];

  const allCounts: Record<string, number> = {};
  const hiddenCounts: Record<string, number> = {};
  const sipseongPillars: SipseongPillar[] = [];

  for (const pillar of pillarsToAnalyze) {
    const isDay = pillar.label === '일주';

    // 천간 십성
    const stemSipseong = isDay ? null : getSipseong(dayIdx, pillar.stemIdx);
    if (stemSipseong) {
      allCounts[stemSipseong.name] = (allCounts[stemSipseong.name] || 0) + 1;
    }

    // 지지 십성 (지지 오행으로 대표 천간 인덱스 추정 - 정기 천간 기준)
    const branchJijang = JIJANG[pillar.branchIdx];
    const mainJijang = branchJijang[branchJijang.length - 1]; // 정기
    const branchSipseong = getSipseong(dayIdx, mainJijang.stemIdx);
    allCounts[branchSipseong.name] = (allCounts[branchSipseong.name] || 0) + 1;

    // 지장간 전체
    const jijangItems = branchJijang.map(entry => {
      const ss = getSipseong(dayIdx, entry.stemIdx);
      hiddenCounts[ss.name] = (hiddenCounts[ss.name] || 0) + (entry.role === '정기' ? 3 : entry.role === '중기' ? 2 : 1);
      return {
        entry,
        sipseong: ss,
        stemName: HEAVENLY_STEMS[entry.stemIdx],
        stemKor: HEAVENLY_STEMS_KOR[entry.stemIdx],
      };
    });

    sipseongPillars.push({
      pillarLabel: pillar.label,
      stemSipseong,
      branchSipseong,
      jijang: jijangItems,
    });
  }

  // 가장 많은 십성
  const dominantName = Object.entries(allCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '비견';
  const hiddenName = Object.entries(hiddenCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '편재';

  return {
    pillars: sipseongPillars,
    dominant: SIPSEONG_MAP[dominantName],
    hidden: SIPSEONG_MAP[hiddenName],
    allCounts,
    hiddenCounts,
  };
}

// 오행 인덱스 export용
export { ELEMENTS_ARR, ELEMENT_IDX, GENERATES, CONTROLS, GENERATED_BY, CONTROLLED_BY };
